/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import PropTypes from 'util/PropTypes';
import classNames from 'classnames';

import { RangeSlider, Checkbox, Separator,  SVGIcon, Label } from '@ba/ba-ui-carbon-toolkit';
import StringResources from '../../../nls/StringResources';
import './relationshipVisView.scss';
import RelationshipInfoTooltip from './RelationshipInfoTooltip';
import getInformation16 from '@ba/ba-graphics/dist/icons-js/get-information_16';

@observer
class RelationshipToolbar  extends Component {
	@observable sliderRange = null;

	/***
	 *@constructor
	 **/
	constructor(props) {
		super(props);
		this.sliderRange = {
			min: this.props.targetModel.computedMinStrength,
			max: this.props.targetModel.computedMaxStrength
		};
	}

	componentWillReceiveProps(newProps) {
		// Update slider state when we have new relationship min-max strength passed in props
		this.sliderRange = {
			min: newProps.targetModel.computedMinStrength,
			max: newProps.targetModel.computedMaxStrength
		};
	}

	/* Render the relationship toolbar which includes filter strength slider and checkbox for view type */
	render() {
		const sliderLabel = StringResources.get('relationshipGraph_filterStrength_label');
		return (
			<div className = "relationship-vis-view__toolbarNew">
				{this._buildInformationMarkup(sliderLabel)}
				{this._buildSliderMarkup(sliderLabel)}
				<Separator orientation='vertical' hMargin={2}/>
				{this._buildCheckboxMarkUp()}
			</div>
		);
	}

	_buildInformationMarkup(sliderLabel) {
		return (
			<div className="relationship-vis-view__toolbarNew__info" tabIndex={0} aria-label={sliderLabel}
				onClick={this._onInfoClick}
				onKeyDown={this._onKeyDown}>
				<SVGIcon iconId={getInformation16.id} style={{ 'vertical-align': 'top' }}/>
				<div className="relationship-vis-view__toolbarNew__infoTooltip">
					<RelationshipInfoTooltip model={this.props.targetModel.relationshipVM.getInfoTooltipModel()} />
				</div>
			</div>
		);
	}

	_onKeyDown = (event) => {
		switch (event.key) {
			case ' ': // space
			case 'Enter':
				this._onInfoClick(event);
		}
	}

	_onInfoClick = (event) => {
		// avoid reacting to the click on X icon when the info flyout is opened
		if (event && event.target && event.target.classList && event.target.classList.contains('relationship-vis-view__toolbarNew__info')) {
			this.props.targetModel.relationshipVM.toggleInfoTooltip(event.target);
		}
	}

	_buildCheckboxMarkUp() {
		const label = StringResources.get('relationshipGraph_secondaryRelationship_label');
		const alignment = 'left';
		return (
			<div className = "relationship-vis-view__toolbarNew__secondaryRelationship-checkbox" tabIndex={0}>
				<Checkbox
					label = {label}
					align = {alignment}
					onChange={() => {
						this.props.targetModel.setShowPrimaryRelationshipView(!this.props.targetModel.showPrimaryRelationshipView);
					}}
					checked={!this.props.targetModel.showPrimaryRelationshipView}
					size='small'
				/>
			</div>
		);
	}

	_buildSliderMarkup(sliderLabel) {
		const classes = classNames({
			'relationship-vis-view__toolbarNew__sliderParent' : true
		});
		const toPercent = (number) => {
			return number.toString() + '%';
		};
		return (
			<div className = {classes}>
				<Label className="relationship-vis-view__toolbarNew__sliderParent__sliderLabel" label={sliderLabel}/>
				<div className = 'relationship-vis-view__toolbarNew__sliderParent__sliderContainer'>
					<RangeSlider
						showTooltip
						showBoundaryLabels
						className = "relationship-vis-view__toolbarNew__sliderParent__slider__range"
						values={[this.sliderRange.min, this.sliderRange.max]}
						labelFormatter={toPercent}
						onChange={values => this._onValueChange(...values)}
						onInputComplete={() => this._onInputComplete()}
						size='small'
						min={10} max={100} />
				</div>
			</div>
		);
	}

	_onValueChange(min, max) {
		this.sliderRange = { min, max };
	}

	_onInputComplete() {
		this.props.targetModel.setFilterStrength({ min: this.sliderRange.min, max: this.sliderRange.max });
	}

}

RelationshipToolbar.propTypes = {
	targetModel: PropTypes.object.isRequired
};


export default RelationshipToolbar ;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipToolbar.js